import {
  ArrowLeft, ArrowRight, ArrowUpRight, Boxes, ChevronRight, CircleDot, Database, ExternalLink,
  GitCommitHorizontal, Layers3, MonitorCog, MoveRight, ServerCog, ShieldCheck, TestTube2,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useGraphData } from "./GraphDataContext";
import { CapabilityMark } from "../components/CapabilityMark";
import { layerAgentById, layerAgents } from "../data/layerAgents";
import { websiteManagementAgentById, websiteManagementAgents } from "../data/websiteManagementAgents";
import { filterNodes, visibleEdges } from "../domain/graph";
import type { GraphMode, TechnologyNode } from "../domain/model";
import { LayerLeadConsole, LayerLeadRoster } from "../features/agents/LayerLeads";
import { GraphCanvas } from "../features/graph/GraphCanvas";
import { GraphControls } from "../features/graph/GraphControls";
import { MobileNodeList } from "../features/graph/MobileNodeList";
import { NodeInspector } from "../features/graph/NodeInspector";
import { TraceLab, TypeLab } from "../features/lab/LabExperiments";

export function HomePage() {
  const { snapshot } = useGraphData();
  const navigate = useNavigate();
  if (!snapshot) return null;
  return (
    <>
      <section className="home-hero">
        <img className="hero-team-image" src="/assets/layer-agents/pweb-layer-leads-hero-v1.png" alt="Nine adult PWEB Layer Leads in the full-stack command center" fetchPriority="high" />
        <div className="hero-copy">
          <p className="eyebrow">PWEB / NINE LAYER LEADS / 47 CAPABILITY NODES</p>
          <h1>One graph.<br />Nine accountable leads.</h1>
          <p className="hero-support">A living full-stack system where every layer has an owner, every node has a state and every capability claim requires evidence.</p>
          <div className="hero-actions">
            <Link className="command-button" to="/graph">Open civilization graph <ArrowRight size={16} /></Link>
            <Link className="text-link" to="/system">Inspect the architecture <ChevronRight size={15} /></Link>
          </div>
        </div>
        <div className="hero-layer-index" aria-label="Nine graph layers">
          {snapshot.nodes.reduce<string[]>((layers, node) => layers.includes(node.layer) ? layers : [...layers, node.layer], []).map((layer, index) => (
            <span key={layer}><b>{String(index + 1).padStart(2, "0")}</b>{layer}</span>
          ))}
        </div>
      </section>
      <section className="system-stats" aria-label="Live system statistics">
        {[
          [snapshot.stats.nodes, "Nodes"], [snapshot.stats.built, "Built+"], [snapshot.stats.shipped, "Shipped+"],
          [snapshot.stats.evidenced, "Evidenced+"], [snapshot.stats.activeProjects, "Active projects"],
        ].map(([value, label]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
        <div className="data-origin"><CircleDot size={14} /><span>{snapshot.source.toUpperCase()} / {new Date(snapshot.generatedAt).toLocaleDateString("en-CA")}</span></div>
      </section>
      <LayerLeadRoster snapshot={snapshot} />
      <section className="home-graph" aria-labelledby="system-map-title">
        <div className="section-heading">
          <div><p className="eyebrow">LIVE SYSTEM MAP</p><h2 id="system-map-title">One system. Multiple computation layers.</h2></div>
          <Link className="text-link" to="/graph">Full graph <ArrowUpRight size={15} /></Link>
        </div>
        <GraphCanvas nodes={snapshot.nodes} edges={snapshot.edges} selectedId={null} mode="STACK" compact onSelect={(node) => navigate(`/graph?node=${node.id}`)} />
      </section>
      <section className="loop-band">
        {['Need','Learn','Build','Measure','Ship','Evidence','Expand'].map((item, index) => <div key={item}><span>0{index + 1}</span><strong>{item}</strong>{index < 6 && <MoveRight aria-hidden="true" />}</div>)}
      </section>
      <section className="home-columns">
        <div className="home-column-intro"><p className="eyebrow">CURRENT FRONTIER</p><h2>Evidence changes the map.</h2><p>Capability advances only when code, tests, deployment or external review supports the transition.</p></div>
        <div className="frontier-list">
          {snapshot.learningActions.filter((item) => item.status === "active").map((action) => {
            const node = snapshot.nodes.find((item) => item.id === action.nodeId);
            return <Link key={action.id} to={`/graph?node=${action.nodeId}&mode=LEARNING`}><span>{node?.layer}</span><strong>{action.task}</strong><ArrowUpRight size={17} /></Link>;
          })}
        </div>
      </section>
    </>
  );
}

export function GraphPage() {
  const { snapshot } = useGraphData();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [layer, setLayer] = useState(() => params.get("layer") ?? "");
  const [state, setState] = useState("");
  const [projectId, setProjectId] = useState("");
  if (!snapshot) return null;
  const mode = (params.get("mode") as GraphMode | null) ?? "STACK";
  const selectedId = params.get("node");
  const selectedNode = snapshot.nodes.find((item) => item.id === selectedId) ?? null;
  const filtered = filterNodes(snapshot, { query, layer, state, projectId, mode });
  const edges = visibleEdges(filtered, snapshot.edges);

  function selectNode(node: TechnologyNode) {
    const next = new URLSearchParams(params);
    next.set("node", node.id);
    next.set("mode", mode);
    setParams(next);
  }

  function setMode(nextMode: GraphMode) {
    const next = new URLSearchParams(params);
    next.set("mode", nextMode);
    if (nextMode === "PROJECT" && !projectId) setProjectId("pweb-001");
    setParams(next);
  }

  function setLayerFilter(nextLayer: string) {
    setLayer(nextLayer);
    const next = new URLSearchParams(params);
    if (nextLayer) next.set("layer", nextLayer);
    else next.delete("layer");
    if (selectedNode && nextLayer && selectedNode.layer !== nextLayer) next.delete("node");
    setParams(next);
  }

  return (
    <section className="graph-page">
      <header className="page-heading compact-heading"><div><p className="eyebrow">FULL-STACK CIVILIZATION GRAPH</p><h1>Architecture is a network of decisions.</h1></div><p>{filtered.length} / {snapshot.nodes.length} visible nodes</p></header>
      <GraphControls snapshot={snapshot} mode={mode} query={query} layer={layer} state={state} projectId={projectId} onMode={setMode} onQuery={setQuery} onLayer={setLayerFilter} onState={setState} onProject={setProjectId} />
      <LayerLeadConsole snapshot={snapshot} activeLayer={layer} onLayer={setLayerFilter} />
      <div className={selectedNode ? "graph-workbench inspector-open" : "graph-workbench"}>
        <GraphCanvas nodes={filtered} edges={edges} selectedId={selectedNode?.id ?? null} mode={mode} onSelect={selectNode} />
        <MobileNodeList nodes={filtered} onSelect={selectNode} />
        {selectedNode && <NodeInspector node={selectedNode} snapshot={snapshot} onClose={() => { const next = new URLSearchParams(params); next.delete("node"); setParams(next); }} />}
      </div>
    </section>
  );
}

export function LayerAgentPage() {
  const { snapshot } = useGraphData();
  const { agentId = "" } = useParams();
  if (!snapshot) return null;
  const agent = layerAgentById(agentId);
  if (!agent) return <NotFoundPage />;
  const ownedNodes = snapshot.nodes.filter((node) => node.layer === agent.layer);
  const agentIndex = layerAgents.findIndex((item) => item.id === agent.id);
  const previousAgent = layerAgents[(agentIndex - 1 + layerAgents.length) % layerAgents.length] ?? agent;
  const nextAgent = layerAgents[(agentIndex + 1) % layerAgents.length] ?? agent;

  return (
    <section className="agent-profile-page" style={{ "--agent-accent": agent.accent } as React.CSSProperties}>
      <div className="agent-profile-visual">
        <img src={agent.image} alt={`Full-body portrait of ${agent.name}, PWEB ${agent.layer} Layer Lead`} fetchPriority="high" />
        <span>{agent.index} / 09</span>
      </div>
      <div className="agent-profile-content">
        <Link className="text-link agent-back-link" to="/"><ArrowLeft size={15} /> All Layer Leads</Link>
        <p className="eyebrow">{agent.layer.toUpperCase()} LAYER / {ownedNodes.length} OWNED NODES</p>
        <h1>{agent.name}</h1>
        <h2>{agent.role}</h2>
        <p className="agent-mandate">{agent.mandate}</p>
        <dl className="agent-specs">
          <div><dt>Layer</dt><dd>{agent.layer}</dd></div>
          <div><dt>Hair system</dt><dd>{agent.hair} / {agent.hairLength}</dd></div>
          <div><dt>Ownership</dt><dd>{ownedNodes.length} capability nodes</dd></div>
          <div><dt>Graph contract</dt><dd>One lead / one layer</dd></div>
        </dl>
        <div className="agent-node-list" aria-label={`${agent.name} owned nodes`}>
          {ownedNodes.map((node) => (
            <Link key={node.id} to={`/graph?layer=${encodeURIComponent(agent.layer)}&node=${node.id}`}>
              <span>{node.capabilityState}</span><strong>{node.name}</strong><ArrowUpRight size={15} />
            </Link>
          ))}
        </div>
        <Link className="command-button agent-graph-link" to={`/graph?layer=${encodeURIComponent(agent.layer)}`}>
          Open {agent.layer} layer <ArrowRight size={16} />
        </Link>
        <nav className="agent-profile-pagination" aria-label="Layer Lead profiles">
          <Link to={`/agents/${previousAgent.id}`}><span>Previous</span><strong>{previousAgent.name} / {previousAgent.layer}</strong></Link>
          <Link to={`/agents/${nextAgent.id}`}><span>Next</span><strong>{nextAgent.name} / {nextAgent.layer}</strong></Link>
        </nav>
      </div>
    </section>
  );
}

export function LearnPage() {
  const { snapshot } = useGraphData();
  const [scope, setScope] = useState<"active" | "queued" | "all">("active");
  if (!snapshot) return null;
  const actions = snapshot.learningActions.filter((item) => scope === "all" || item.status === scope);
  const evidenced = snapshot.nodes.filter((node) => ["EVIDENCED", "DEFENDED"].includes(node.capabilityState)).length;
  const active = snapshot.learningActions.filter((item) => item.status === "active").length;
  return (
    <section className="page-section">
      <header className="page-heading"><div><p className="eyebrow">PWEB / FULL-STACK MASTERY TRACK</p><h1>Build the system, then earn the capability.</h1></div><p>PWEB is the practical map: learn through a real product, keep evidence, and only add technology when a measured constraint requires it.</p></header>
      <section className="mastery-overview" aria-label="PWEB full-stack learning overview">
        <div><strong>{snapshot.nodes.length}</strong><span>Mapped capabilities</span></div>
        <div><strong>{evidenced}</strong><span>Evidenced or defended</span></div>
        <div><strong>{active}</strong><span>Active training actions</span></div>
        <div><strong>9</strong><span>Accountable learning layers</span></div>
      </section>
      <section className="mastery-track" aria-labelledby="mastery-track-title">
        <header className="mastery-track-heading">
          <div><p className="eyebrow">THE COMPLETE MAP / LEARN IN ORDER</p><h2 id="mastery-track-title">One system, nine engineering perspectives.</h2></div>
          <p>Start from interface and typed product delivery. Systems, additional runtimes and infrastructure choices remain explicit future branches until profiling or product constraints justify them.</p>
        </header>
        <div className="mastery-layer-list">
          {layerAgents.map((agent) => {
            const nodes = snapshot.nodes.filter((node) => node.layer === agent.layer);
            return (
              <article key={agent.id} style={{ "--agent-accent": agent.accent } as React.CSSProperties}>
                <Link className="mastery-owner" to={`/agents/${agent.id}`} aria-label={`Open ${agent.name}'s ${agent.layer} learning profile`}>
                  <span>{agent.index}</span><strong>{agent.name}</strong><small>{agent.layer}</small>
                </Link>
                <div className="mastery-layer-content">
                  <div><p className="eyebrow">{agent.role}</p><h3>{agent.mandate}</h3></div>
                  <div className="mastery-node-list" aria-label={`${agent.layer} learning capabilities`}>
                    {nodes.map((node) => (
                      <Link key={node.id} to={`/graph?layer=${encodeURIComponent(agent.layer)}&node=${node.id}`}>
                        <span>{node.name}</span><CapabilityMark state={node.capabilityState} />
                      </Link>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <header className="learning-queue-heading"><div><p className="eyebrow">CURRENT TRAINING QUEUE</p><h2>What PWEB should learn next.</h2></div><p>Every entry is linked to a graph node, a concrete deliverable and an evidence gate.</p></header>
      <div className="segmented-control page-segment">{(["active","queued","all"] as const).map((item) => <button key={item} className={scope === item ? "active" : ""} onClick={() => setScope(item)}>{item.toUpperCase()}</button>)}</div>
      <div className="learning-table">
        {actions.map((action, index) => {
          const node = snapshot.nodes.find((item) => item.id === action.nodeId);
          const project = snapshot.projects.find((item) => item.id === action.projectId);
          return (
            <article key={action.id}>
              <span className="row-number">{String(index + 1).padStart(2, "0")}</span>
              <div><p className="eyebrow">{node?.layer} / {action.status}</p><h2>{action.task}</h2><p>{action.reason}</p></div>
              <div className="criteria"><strong>Evidence gate</strong>{action.evidenceRequired.map((item) => <span key={item}>{item}</span>)}</div>
              <div className="learning-meta"><span>{project?.name ?? "UNASSIGNED"}</span>{node && <CapabilityMark state={node.capabilityState} />}</div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function BuildPage() {
  const { snapshot } = useGraphData();
  if (!snapshot) return null;
  return (
    <section className="page-section">
      <header className="page-heading"><div><p className="eyebrow">PROJECT ATLAS</p><h1>Projects are vertical slices through the graph.</h1></div><p>Only PWEB-001 is implemented deeply in V1. Future surfaces remain labeled as ideas.</p></header>
      <div className="project-atlas">
        {snapshot.projects.map((project, index) => (
          <article key={project.id} className={project.id === "pweb-001" ? "featured" : ""}>
            <header><span>{String(index + 1).padStart(2, "0")}</span><strong>{project.status}</strong></header>
            <h2>{project.name}</h2><p>{project.summary}</p>
            <div className="project-stack">{project.nodeIds.slice(0, 9).map((id) => <span key={id}>{snapshot.nodes.find((item) => item.id === id)?.name}</span>)}</div>
            <footer><span>{project.nodeIds.length} nodes / {project.evidenceIds.length} evidence</span>{project.deployUrl ? <a href={project.deployUrl} target="_blank" rel="noreferrer">Open <ExternalLink size={14} /></a> : project.repo ? <a href={project.repo} target="_blank" rel="noreferrer">Source <ExternalLink size={14} /></a> : <span>Not shipped</span>}</footer>
          </article>
        ))}
      </div>
    </section>
  );
}

export function LabPage() {
  return (
    <section className="page-section lab-page">
      <header className="page-heading"><div><p className="eyebrow">INTERFACE LAB / EXPERIMENTS 001-002</p><h1>Visual ambition, measured through interaction.</h1></div><p>Experiments graduate only when they improve a production surface.</p></header>
      <TypeLab />
      <TraceLab />
    </section>
  );
}

export function EvidencePage() {
  const { snapshot } = useGraphData();
  const [selected, setSelected] = useState("all");
  if (!snapshot) return null;
  const evidence = snapshot.evidence.filter((item) => selected === "all" || item.type === selected);
  const types = Array.from(new Set(snapshot.evidence.map((item) => item.type)));
  return (
    <section className="page-section evidence-page">
      <header className="page-heading"><div><p className="eyebrow">EVIDENCE + CAPABILITY MATRIX</p><h1>Narrative equals evidence.</h1></div><p>Facts, verification and inference remain separate.</p></header>
      <div className="evidence-summary">
        <div><strong>{snapshot.evidence.length}</strong><span>Artifacts</span></div>
        <div><strong>{snapshot.evidence.filter((item) => item.verification === "external").length}</strong><span>External</span></div>
        <div><strong>{snapshot.evidence.filter((item) => item.verification === "unverified").length}</strong><span>Pending</span></div>
        <label><span>FILTER</span><select value={selected} onChange={(event) => setSelected(event.target.value)}><option value="all">ALL TYPES</option>{types.map((item) => <option key={item}>{item}</option>)}</select></label>
      </div>
      <div className="evidence-ledger">
        {evidence.map((item) => (
          <article key={item.id}>
            <span className={`verification ${item.verification}`}>{item.verification}</span>
            <div><p className="eyebrow">{item.type} / {item.repo}</p><h2>{item.title}</h2><p>{item.notes}</p></div>
            <div className="evidence-nodes">{item.nodeIds.map((id) => <span key={id}>{snapshot.nodes.find((node) => node.id === id)?.name ?? id}</span>)}</div>
            {item.url ? <a className="icon-button" href={item.url} target="_blank" rel="noreferrer" aria-label={`Open ${item.title}`}><ArrowUpRight /></a> : <span className="pending-link">PENDING</span>}
          </article>
        ))}
      </div>
    </section>
  );
}

const architecture = [
  { icon: MonitorCog, label: "React + TypeScript", detail: "Typed interface state and graph rendering", source: "src/" },
  { icon: Layers3, label: "Hono API", detail: "Validated HTTP boundary and error contract", source: "worker/index.ts" },
  { icon: ServerCog, label: "Worker Runtime", detail: "Edge execution, structured logs and security headers", source: "wrangler.jsonc" },
  { icon: Database, label: "D1 / SQLite", detail: "Persistent nodes, edges, projects, evidence and actions", source: "migrations/" },
  { icon: TestTube2, label: "Verification", detail: "Unit, integration, E2E and performance budgets", source: "tests/" },
  { icon: ShieldCheck, label: "Cloudflare", detail: "One deployed full-stack Worker surface", source: "deploy" },
];

export function SystemPage() {
  return (
    <section className="page-section system-page">
      <header className="page-heading"><div><p className="eyebrow">SELF-EXPLAINING SOFTWARE</p><h1>PWEB teaches Web by exposing itself.</h1></div><p>Frontend, API and storage boundaries stay visible.</p></header>
      <div className="architecture-flow">
        <div className="architecture-user"><span>YOU</span><strong>Intent + interaction</strong></div>
        {architecture.map(({ icon: Icon, label, detail, source }, index) => (
          <div className="architecture-step" key={label}>
            <span className="architecture-index">0{index + 1}</span><Icon aria-hidden="true" />
            <div><h2>{label}</h2><p>{detail}</p></div><code>{source}</code>
          </div>
        ))}
      </div>
      <div className="system-contracts">
        <section><p className="eyebrow">READ PATH</p><h2>{"Browser -> API -> Repository -> D1"}</h2><p>The browser does not own canonical graph data. The Worker reconstructs domain objects from normalized D1 rows.</p></section>
        <section><p className="eyebrow">CLAIM PATH</p><h2>{"Artifact -> Verification -> Gate -> State"}</h2><p>The deterministic evaluator can recommend one bounded transition. Human review retains final authority for strong claims.</p></section>
        <section><p className="eyebrow">FEDERATION PATH</p><h2>{"Owning repo -> Adapter -> Reference"}</h2><p>PWEB stores normalized references and freshness metadata, never silent copies of private canonical truth.</p></section>
      </div>
    </section>
  );
}

export function WebPlatformPage() {
  return (
    <section className="web-platform-page">
      <header className="platform-hero">
        <img src="/assets/website-management-agents/pweb-platform-team-v1.webp" alt="Three adult PWEB Website Platform agents standing full-body in the web operations studio" fetchPriority="high" />
        <div className="platform-hero-copy">
          <p className="eyebrow">PWEB / WEBSITE PLATFORM / V1</p>
          <h1>Pengyi's presence on the web.</h1>
          <p>Web learning remains intact. The new platform layer owns how verified domain evidence is designed, built, audited, deployed and maintained.</p>
        </div>
      </header>

      <div className="platform-definition" aria-label="PWEB dual mission">
        <div><span>01</span><strong>Web Learning</strong><p>47 capability nodes, nine layers, labs, evidence gates and deployed full-stack practice.</p></div>
        <div><span>02</span><strong>Website Platform</strong><p>Registry, private control surface, personal website, shared conventions, release gates and lifecycle ownership.</p></div>
      </div>

      <section className="platform-boundary" aria-labelledby="platform-boundary-title">
        <div><p className="eyebrow">OWNERSHIP BOUNDARY</p><h2 id="platform-boundary-title">Domain repos own what.<br />PWEB owns how it lives on the web.</h2></div>
        <ol>
          <li><span>INPUT</span><strong>Verified domain evidence</strong></li>
          <li><span>CONTROL</span><strong>Canonical website registry</strong></li>
          <li><span>BUILD</span><strong>Frontend + justified backend</strong></li>
          <li><span>GATE</span><strong>Independent Audit Agent</strong></li>
          <li><span>OUTPUT</span><strong>Verified deployment + log</strong></li>
        </ol>
      </section>

      <section className="platform-agent-section" aria-labelledby="platform-agent-title">
        <header><div><p className="eyebrow">THREE PRIMARY AGENTS</p><h2 id="platform-agent-title">Build, operate, then prove.</h2></div><p>The backend joins only when a feature requires it. Audit remains independent from both builders.</p></header>
        <div className="platform-agent-grid">
          {websiteManagementAgents.map((agent) => (
            <Link key={agent.id} to={`/web-platform/agents/${agent.id}`} className="platform-agent-card" style={{ "--platform-accent": agent.accent } as React.CSSProperties} aria-label={`${agent.name}, ${agent.role} profile`}>
              <div className="platform-agent-image"><img src={agent.image} alt={`Full-body portrait of ${agent.name}, PWEB ${agent.role}`} loading="lazy" /></div>
              <div className="platform-agent-copy"><span>{agent.index} / 03</span><p>{agent.role}</p><h3>{agent.name}</h3><small>{agent.visualKey}</small><ArrowUpRight size={18} aria-hidden="true" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="platform-surfaces">
        <header><p className="eyebrow">V1 SURFACES</p><h2>Public identity and private control stay separate.</h2></header>
        <div><article><span>PUBLIC</span><h3>Personal Website</h3><p>Researcher + engineer + quant information architecture. Claims remain evidence-gated until domain adapters are connected.</p><strong>V1 SKELETON / NOT DEPLOYED</strong></article><article><span>PRIVATE</span><h3>Website Dashboard</h3><p>Registry-driven status, ownership, repository, deployment, audit and next-action view.</p><strong>LOCAL ONLY / AUTH REQUIRED BEFORE REMOTE</strong></article></div>
      </section>

      <section className="platform-workflow" aria-label="Website platform workflow">
        {['Request','Frontend','Backend if required','Integration','Audit','Deploy','Registry','Log'].map((step, index) => <div key={step}><span>{String(index + 1).padStart(2, '0')}</span><strong>{step}</strong></div>)}
      </section>
    </section>
  );
}

export function WebPlatformAgentPage() {
  const { agentId = "" } = useParams();
  const agent = websiteManagementAgentById(agentId);
  if (!agent) return <NotFoundPage />;
  const agentIndex = websiteManagementAgents.findIndex((item) => item.id === agent.id);
  const previousAgent = websiteManagementAgents[(agentIndex - 1 + websiteManagementAgents.length) % websiteManagementAgents.length] ?? agent;
  const nextAgent = websiteManagementAgents[(agentIndex + 1) % websiteManagementAgents.length] ?? agent;

  return (
    <section className="platform-agent-profile" style={{ "--platform-accent": agent.accent } as React.CSSProperties}>
      <div className="platform-agent-profile-image"><img src={agent.image} alt={`Full-body portrait of ${agent.name}, PWEB ${agent.role}`} fetchPriority="high" /><span>{agent.index} / 03</span></div>
      <div className="platform-agent-profile-content">
        <Link className="text-link" to="/web-platform"><ArrowLeft size={15} /> Website Platform</Link>
        <p className="eyebrow">PWEB MANAGEMENT AGENT / {agent.role.toUpperCase()}</p>
        <h1>{agent.name}</h1><h2>{agent.role}</h2><p className="platform-agent-mission">{agent.mission}</p>
        <section><h3>Workflow</h3><div className="platform-agent-flow">{agent.workflow.map((step, index) => <span key={step}>{String(index + 1).padStart(2, '0')} / {step}</span>)}</div></section>
        <section><h3>Responsibilities</h3><ul>{agent.responsibilities.map((item) => <li key={item}>{item}</li>)}</ul></section>
        <section><h3>Quality gates</h3><ul>{agent.qualityGates.map((item) => <li key={item}>{item}</li>)}</ul></section>
        <nav className="agent-profile-pagination" aria-label="Website Platform Agent profiles"><Link to={`/web-platform/agents/${previousAgent.id}`}><span>Previous</span><strong>{previousAgent.name} / {previousAgent.role}</strong></Link><Link to={`/web-platform/agents/${nextAgent.id}`}><span>Next</span><strong>{nextAgent.name} / {nextAgent.role}</strong></Link></nav>
      </div>
    </section>
  );
}

export function ChangelogPage() {
  return (
    <section className="page-section changelog-page">
      <header className="page-heading"><div><p className="eyebrow">CHANGELOG</p><h1>The system records how it learns.</h1></div></header>
      <div className="changelog-entry"><time>2026-08-14</time><span><GitCommitHorizontal /></span><div><p className="eyebrow">V0.1 / V1 VERTICAL SLICE</p><h2>PWEB becomes a full-stack system.</h2><p>Typed civilization graph, D1 repository, Worker API, four graph modes, evidence gate, project-native learning, two labs, CI and Cloudflare deployment contract.</p></div></div>
    </section>
  );
}

export function NotFoundPage() {
  return <section className="not-found"><p className="eyebrow">404 / OUTSIDE THE GRAPH</p><h1>This surface has no system boundary.</h1><Link className="command-button" to="/"><Boxes size={16} /> Return home</Link></section>;
}
